<template>
    <card title="Compound Preparation">
        <div>
            <!-- Mode Selection (only shown at start if not specified in route) -->
            <div v-if="!batch && !modeInternal">
                <b-row class="mb-3">
                    <b-col>
                        <h4>Select Preparation Mode</h4>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <b-button variant="primary" size="lg" block @click="selectMode('batch')">
                            <i class="fas fa-flask"></i> Create Batch for Inventory
                        </b-button>
                    </b-col>
                    <b-col>
                        <b-button variant="info" size="lg" block @click="selectMode('prescription')">
                            <i class="fas fa-prescription"></i> Prepare for Prescription
                        </b-button>
                    </b-col>
                </b-row>
            </div>

            <!-- Batch Initialization -->
            <div v-if="modeInternal && !batch">
                <b-row class="mb-3">
                    <b-col>
                        <h4>{{ modeTitle }}</h4>
                    </b-col>
                </b-row>
                <b-row v-if="modeInternal === 'batch'" align-v="end">
                    <b-col cols="6">
                        <SearchComponent v-model="compoundSearch" label="Select Compound" search-u-r-l="/Compound"
                            @input="onCompoundSelected" />
                    </b-col>
                    <b-col cols="3">
                        <fg-input v-model.number="batchSize" label="Batch Size" type="number" step="0.01" />
                    </b-col>
                    <b-col cols="3" class="pt-4">
                        <b-button variant="success" @click="startBatch" :disabled="!selectedCompound || !batchSize || !selectedCompound.compoundID || !selectedCompound.steps || selectedCompound.steps.length === 0">
                            Start Preparation
                        </b-button>
                    </b-col>
                </b-row>
                <b-row v-if="modeInternal === 'prescription'">
                    <b-col cols="8">
                        <SearchComponent v-model="selectedPrescription" label="Select Prescription" search-u-r-l="/Prescription"
                            @input="onPrescriptionSelected" />
                        <small v-if="selectedPrescription && selectedPrescription.rxNumber" class="text-muted">
                            <strong>Dispensed Quantity:</strong> {{ selectedPrescription.dispensed }} {{ selectedPrescription.productNameShort }}
                        </small>
                    </b-col>
                    <b-col cols="4" class="pt-4">
                        <b-button variant="success" @click="startBatchForPrescription" :disabled="!selectedPrescription || !selectedPrescription.rxNumber || !selectedCompound || !selectedCompound.compoundID || !selectedCompound.steps || selectedCompound.steps.length === 0">
                            Start Preparation
                        </b-button>
                    </b-col>
                </b-row>
            </div>

            <!-- Batch Preparation Workflow -->
            <div v-if="batch && currentStep">
                <b-row class="mb-3">
                    <b-col>
                        <h4>{{ selectedCompound?.name }}</h4>
                        <p class="text-muted">
                            Batch: {{ batch.batchNumber }} | Size: {{ batch.batchSize }} {{ selectedCompound?.unitOfMeasure }}
                            <span v-if="modeInternal === 'prescription' && batch.rxNumber">
                                | Prescription: {{ batch.storeId }}-{{ batch.rxNumber }}-{{ batch.rfNumber || 0 }}
                            </span>
                        </p>
                    </b-col>
                </b-row>

                <!-- Ingredients and Steps Row -->
                <b-row class="mb-3">
                    <!-- Ingredients Needed (Left Half) -->
                    <b-col cols="6">
                        <b-alert variant="warning" :show="selectedCompound?.ingredients?.length > 0 && !selectedCompound?.packageSize">
                            <i class="fas fa-exclamation-triangle" />
                            <strong>Package Size Not Set</strong><br />
                            This compound does not have a Package Size configured. Please edit the compound record and set the Package Size
                            to enable ingredient quantity scaling. Without this, the system cannot calculate the correct ingredient amounts for this batch.
                        </b-alert>

                        <b-card v-if="scaledIngredients.length > 0">
                            <b-card-title>
                                <i class="fas fa-flask" /> Ingredients Needed
                            </b-card-title>

                            <!-- Preset Selector -->
                            <b-row v-if="presets.length > 0" class="mb-2">
                                <b-col cols="6">
                                    <label class="mb-0"><small><strong>Ingredient Preset:</strong></small></label>
                                    <b-form-select
                                        v-model="selectedPresetId"
                                        size="sm"
                                        @change="applyPreset"
                                    >
                                        <option :value="null">Default Formula</option>
                                        <option v-for="p in presets" :key="p.id" :value="p.id">
                                            {{ p.name }}{{ p.isDefault ? ' (Default)' : '' }}
                                        </option>
                                    </b-form-select>
                                </b-col>
                            </b-row>

                            <b-alert :show="hasIngredientFromDifferentStore" variant="warning" class="mb-2">
                                <i class="fas fa-exclamation-triangle"></i>
                                <strong>Cross-Store Ingredient:</strong> One or more selected lots are from a different store than the batch destination.
                            </b-alert>

                            <b-table-simple hover small class="ingredients-table">
                                <b-thead>
                                    <b-tr>
                                        <b-th>Ingredient</b-th>
                                        <b-th class="text-right">Formula Qty</b-th>
                                        <b-th class="text-right">{{ activityFactorEnabled ? 'Scaled Qty' : 'Qty Needed' }}</b-th>
                                        <template v-if="activityFactorEnabled">
                                            <b-th class="text-right">Activity Factor</b-th>
                                            <b-th class="text-right">Weigh-Out</b-th>
                                        </template>
                                        <b-th>Lot Number</b-th>
                                        <b-th>Expiration</b-th>
                                    </b-tr>
                                </b-thead>
                                <b-tbody>
                                    <b-tr v-for="(ingredient, index) in scaledIngredients" :key="index"
                                        :class="{ 'ingredient-row--overridden': activityFor(ingredient)?.override }">
                                        <b-td>
                                            <span>{{ ingredient.name }}</span>
                                            <b-badge v-if="ingredient.isSwapped" variant="warning" class="ml-1">
                                                <i class="fas fa-exchange-alt"></i> Swapped
                                            </b-badge>
                                            <template v-if="activityFactorEnabled && activityFor(ingredient)">
                                                <b-badge v-if="activityFor(ingredient).override" variant="warning" class="ml-1"
                                                    :title="activityFor(ingredient).override.reason">
                                                    <i class="fas fa-user-edit"></i> Overridden
                                                </b-badge>
                                                <b-badge v-if="activityFor(ingredient).result.missingCoa" variant="warning" class="ml-1"
                                                    title="Flagged for activity factor but no usable COA — factor 1.0 applied">
                                                    <i class="fas fa-exclamation-triangle"></i> No COA
                                                </b-badge>
                                                <div v-if="activityFor(ingredient).coaText" class="small"
                                                    :class="activityFor(ingredient).result.coaNotApplied ? 'text-muted font-italic' : 'text-secondary'">
                                                    COA: {{ activityFor(ingredient).coaText }}
                                                    <span v-if="activityFor(ingredient).result.coaNotApplied">
                                                        (not applied — fixed factor {{ ingredient.fixedConversionFactor }})
                                                    </span>
                                                </div>
                                                <div v-else-if="activityFor(ingredient).result.factorSource === 'FixedFactor'" class="small text-muted font-italic">
                                                    Fixed conversion factor {{ ingredient.fixedConversionFactor }} applied
                                                </div>
                                                <div v-for="(warning, warningIndex) in activityFor(ingredient).warnings" :key="warningIndex"
                                                    class="small text-warning">
                                                    <i class="fas fa-exclamation-triangle" /> {{ warning }}
                                                </div>
                                            </template>
                                        </b-td>
                                        <b-td class="text-right text-nowrap">
                                            {{ formatQty(ingredient.formulaQuantity) }} {{ ingredient.unitOfMeasure }}
                                        </b-td>
                                        <b-td class="text-right text-nowrap">
                                            <strong v-if="!activityFactorEnabled">{{ formatQty(ingredient.scaledQuantity) }} {{ ingredient.unitOfMeasure }}</strong>
                                            <span v-else>{{ formatQty(ingredient.scaledQuantity) }} {{ ingredient.unitOfMeasure }}</span>
                                        </b-td>
                                        <template v-if="activityFactorEnabled && activityFor(ingredient)">
                                            <b-td class="text-right text-nowrap">
                                                <span v-if="activityFor(ingredient).flagged"
                                                    :class="{ 'text-muted': activityFor(ingredient).result.factorSource === 'None' }">
                                                    {{ formatFactor(activityFor(ingredient).result.activityFactor) }}
                                                </span>
                                                <span v-else class="text-muted" title="Activity factor is not applied to this ingredient">—</span>
                                            </b-td>
                                            <b-td class="text-right text-nowrap weigh-out-cell">
                                                <strong class="weigh-out">
                                                    {{ formatQty(activityFor(ingredient).result.effectiveQuantity) }} {{ ingredient.unitOfMeasure }}
                                                </strong>
                                                <div v-if="activityFor(ingredient).override" class="small text-muted">
                                                    computed <s>{{ formatQty(activityFor(ingredient).result.adjustedQuantity) }}</s>
                                                </div>
                                                <b-button variant="link" size="sm" class="p-0 small" @click="openOverride(ingredient)">
                                                    {{ activityFor(ingredient).override ? 'Edit override' : 'Override' }}
                                                </b-button>
                                            </b-td>
                                        </template>
                                        <b-td>
                                            <b-form-select
                                                v-if="getIngredientLots(ingredient).length > 0"
                                                :value="selectedLotIds[ingredient.compoundIngredientID] || null"
                                                size="sm"
                                                :options="buildIngredientLotOptions(ingredient)"
                                                class="mb-1"
                                                @change="onLotSelectedForIngredient(ingredient, $event)"
                                            />
                                            <input
                                                v-if="!selectedLotIds[ingredient.compoundIngredientID]"
                                                type="text"
                                                class="form-control form-control-sm"
                                                v-model="ingredient.lotNumber"
                                                placeholder="Lot #" />
                                        </b-td>
                                        <b-td>
                                            <input
                                                v-if="!selectedLotIds[ingredient.compoundIngredientID]"
                                                type="date"
                                                class="form-control form-control-sm"
                                                v-model="ingredient.expirationDate" />
                                        </b-td>
                                    </b-tr>
                                </b-tbody>
                            </b-table-simple>
                            <small class="text-muted">
                                <i class="fas fa-info-circle" />
                                Quantities scaled from package size ({{ selectedCompound?.packageSize }} {{ selectedCompound?.unitOfMeasure }})
                                to batch size ({{ batch.batchSize }} {{ selectedCompound?.unitOfMeasure }})<template v-if="activityFactorEnabled">;
                                <strong>Weigh-Out</strong> is the scaled quantity adjusted by the selected lot's activity factor
                                (or the manual override) — weigh that amount</template>
                            </small>
                        </b-card>
                    </b-col>

                    <!-- Step List (Right Half) -->
                    <b-col cols="6">
                        <b-card>
                            <b-card-title>
                                <i class="fas fa-list-ol" /> Preparation Steps
                            </b-card-title>
                            <b-list-group>
                                <b-list-group-item
                                    v-for="(step, index) in batch.steps"
                                    :key="step.id"
                                    :variant="index < currentStepIndex ? 'success' : index === currentStepIndex ? 'primary' : ''"
                                    class="d-flex align-items-center">
                                    <div class="mr-3">
                                        <i v-if="index < currentStepIndex" class="fas fa-check-circle text-success" />
                                        <i v-else-if="index === currentStepIndex" class="fas fa-arrow-circle-right text-primary" />
                                        <i v-else class="far fa-circle text-muted" />
                                    </div>
                                    <div class="flex-grow-1">
                                        <strong>Step {{ step.stepNumber }}:</strong> {{ step.description }}
                                        <div v-if="step.compoundIngredientID != null" class="small text-muted">
                                            <i class="fas fa-link" />
                                            {{ linkedStepWeightFor(step)?.ingredient?.name || step.linkedIngredientName || 'Linked ingredient' }}<template
                                                v-if="linkedStepWeightFor(step)?.shareText"> · {{ linkedStepWeightFor(step)?.shareText }}</template>
                                        </div>
                                        <div v-if="index < currentStepIndex" class="small text-muted">
                                            <i class="fas fa-check" /> Completed
                                        </div>
                                        <div v-else-if="index === currentStepIndex" class="small text-primary">
                                            <i class="fas fa-spinner fa-pulse" /> In Progress
                                        </div>
                                    </div>
                                </b-list-group-item>
                            </b-list-group>
                        </b-card>
                    </b-col>
                </b-row>

                <!-- Current Step Card -->
                <b-card>
                    <b-card-title>
                        <b-badge variant="primary">Step {{ currentStep.stepNumber }}</b-badge>
                        {{ currentStep.description }}
                    </b-card-title>

                    <!-- Weight Entry (if required) -->
                    <div v-if="currentStepConfig && currentStepConfig.requireWeight" class="mt-3">
                        <div v-if="linkedStepWeight" class="linked-step mb-2 p-2 rounded">
                            <i class="fas fa-link text-muted" />
                            <template v-if="linkedStepWeight.ingredient">
                                Weigh-out of <strong>{{ linkedStepWeight.ingredient.name }}</strong><template
                                    v-if="linkedStepWeight.shareText"> — {{ linkedStepWeight.shareText }}</template>
                                <span v-if="linkedStepWeight.activity" class="text-muted">
                                    ({{ formatQty(linkedStepWeight.activity.result.effectiveQuantity) }} {{ linkedStepWeight.ingredient.unitOfMeasure }}<template
                                        v-if="linkedStepWeight.activity.override"> — overridden</template>)
                                </span>
                                <div class="small text-muted">
                                    <i class="fas fa-vial" /> {{ describeStepLot(linkedStepWeight) }}
                                </div>
                            </template>
                            <template v-else>
                                <span class="text-danger">This step weighs out an ingredient that is no longer in the compound's formula.</span>
                            </template>
                        </div>
                        <b-row>
                            <b-col cols="4">
                                <fg-input v-model.number="currentStep.actualWeight" label="Actual Weight"
                                    type="number" step="0.01" @input="validateWeight" />
                            </b-col>
                            <b-col cols="8">
                                <div class="mt-4">
                                    <template v-if="scaledExpectedWeight !== null">
                                        <strong>Expected:</strong> {{ formatQty(scaledExpectedWeight) }} {{ expectedWeightUnit }}
                                        <br />
                                    </template>
                                    <template v-if="weightRange">
                                        <strong>Acceptable Range:</strong>
                                        {{ formatQty(weightRange.min) }} - {{ formatQty(weightRange.max) }} {{ expectedWeightUnit }}
                                        <br />
                                    </template>
                                    <b-alert v-else variant="warning" show class="py-2 px-3 mb-2">
                                        <i class="fas fa-exclamation-triangle" />
                                        <strong>This step cannot be completed.</strong>
                                        Its weight cannot be checked against an acceptable range:
                                        <template v-if="scaledExpectedWeight === null && linkedStepWeight">
                                            the linked ingredient's weigh-out could not be calculated — the ingredient may have been
                                            removed from the formula, or the compound has no Package Size set.
                                        </template>
                                        <template v-else-if="scaledExpectedWeight === null">
                                            the expected weight for this batch could not be calculated, most often because
                                            the compound has no Package Size set.
                                        </template>
                                        <template v-else>
                                            no Weight Tolerance is configured for the step.
                                        </template>
                                        Correct the compound record, then reopen this batch.
                                    </b-alert>
                                    <b-badge v-if="currentStep.actualWeight"
                                        :variant="currentStep.withinTolerance ? 'success' : 'danger'">
                                        {{ currentStep.withinTolerance ? '✓ Within Tolerance' : '✗ Out of Tolerance' }}
                                    </b-badge>
                                </div>
                            </b-col>
                        </b-row>
                    </div>

                    <!-- Photo Capture (if required) -->
                    <div v-if="currentStepConfig && currentStepConfig.requirePicture" class="mt-3">
                        <b-row>
                            <b-col>
                                <h5>Photo Documentation</h5>
                                <div v-if="!currentStepPhotoPreview">
                                    <input type="file" accept="image/*" @change="onPhotoSelected" ref="photoInput" class="mb-2" />
                                </div>
                                <div v-else>
                                    <img :src="currentStepPhotoPreview" alt="Step Photo" class="img-thumbnail mb-2" style="max-width: 400px;" />
                                    <br />
                                    <b-button size="sm" variant="outline-danger" @click="clearPhoto">
                                        <i class="fas fa-trash" /> Remove Photo
                                    </b-button>
                                </div>
                            </b-col>
                        </b-row>
                    </div>

                    <!-- Navigation Buttons -->
                    <b-row class="mt-4">
                        <b-col>
                            <b-button variant="outline-primary" @click="previousStep" :disabled="currentStepIndex === 0">
                                <i class="fas fa-arrow-left" /> Previous
                            </b-button>
                            <b-button v-if="currentStepIndex < (batch.steps?.length || 0) - 1"
                                variant="primary" @click="nextStep" :disabled="!canProceed">
                                Next <i class="fas fa-arrow-right" />
                            </b-button>
                            <b-button v-else variant="success" @click="completeBatch" :disabled="!canProceed">
                                <i class="fas fa-check" /> Complete Batch
                            </b-button>
                        </b-col>
                    </b-row>
                </b-card>
            </div>

            <!-- Manual weigh-out override -->
            <b-modal v-model="showOverrideModal" :title="`Override Weigh-Out — ${overrideTarget?.name || ''}`" hide-footer>
                <template v-if="overrideTarget && overrideTargetActivity">
                    <p class="mb-2">
                        Computed weigh-out:
                        <strong>{{ formatQty(overrideTargetActivity.result.adjustedQuantity) }} {{ overrideTarget.unitOfMeasure }}</strong>
                        <small class="text-muted">
                            ({{ formatQty(overrideTarget.scaledQuantity) }} {{ overrideTarget.unitOfMeasure }} scaled
                            <template v-if="overrideTargetActivity.flagged">
                                ÷ activity factor {{ formatFactor(overrideTargetActivity.result.activityFactor) }}
                                <template v-if="overrideTargetActivity.result.factorSource === 'FixedFactor'">
                                    from the fixed conversion factor {{ overrideTarget.fixedConversionFactor }}
                                </template>
                                <template v-else-if="overrideTargetActivity.result.factorSource === 'Coa'">from the lot's COA</template>
                                <template v-else>— no usable COA</template>
                            </template>
                            <template v-else>— activity factor not applied to this ingredient</template>)
                        </small>
                    </p>
                    <b-alert show variant="warning" class="py-2 px-3">
                        <i class="fas fa-exclamation-triangle" />
                        An override replaces the computed weigh-out and routes this batch for pharmacist verification.
                        Both values and your reason are kept on the batch record.
                    </b-alert>
                    <fg-input v-model="overrideQuantityInput" type="number" step="any" min="0" required>
                        <template #label>
                            Override weigh-out ({{ overrideTarget.unitOfMeasure }}) <span class="text-danger">*</span>
                        </template>
                    </fg-input>
                    <b-form-group class="mb-2">
                        <template #label>Reason <span class="text-danger">*</span></template>
                        <b-form-textarea v-model="overrideReasonInput" rows="2" maxlength="500"
                            placeholder="Why the computed weigh-out is not being used" />
                    </b-form-group>
                    <div class="d-flex" style="gap: 8px;">
                        <b-button variant="warning" :disabled="!canApplyOverride" @click="applyOverride">Apply Override</b-button>
                        <b-button v-if="overrideTargetActivity.override" variant="outline-danger" @click="clearOverride()">Clear Override</b-button>
                        <b-button variant="secondary" class="ml-auto" @click="showOverrideModal = false">Cancel</b-button>
                    </div>
                </template>
            </b-modal>

            <!-- Batch Completion Form -->
            <b-modal v-model="showCompletionModal" title="Complete Batch" size="lg" hide-footer>
                <b-alert :show="!hasSupportedClientService" variant="danger">
                    <div class="d-flex" style="gap: 6px;">
                        <i class="fa fa-warning"></i>
                        <strong>
                            Supported version of Scriptly Client Service not found.
                        </strong>
                    </div>
                </b-alert>
                <b-alert :show="reviewHoldReasons.length > 0" variant="warning">
                    <i class="fas fa-flag" />
                    <strong>Pharmacist review required.</strong>
                    {{ reviewHoldReasons.join(' ') }}
                    This batch will be held for pharmacist verification before inventory is posted or the
                    prescription is updated.
                </b-alert>

                <b-row>
                    <b-col cols="6">
                        <fg-input v-model="lotNumber" label="Lot Number" type="text" />
                    </b-col>
                    <b-col cols="6">
                        <fg-input v-model="beyondUseDate" label="Beyond Use Date" type="date" />
                    </b-col>
                </b-row>
                <b-row v-if="modeInternal === 'batch'" class="mt-2">
                    <b-col cols="4">
                        <SearchComponent v-model="selectedStore" label="Store" search-u-r-l="/Store" prepopulate required
                            :autoselect="autoselectStore" :state="storeFieldState" />
                    </b-col>
                    <b-col cols="4">
                        <SearchComponent v-model="selectedProgram" label="Program (Optional)" search-u-r-l="/Program" clearable prepopulate/>
                    </b-col>
                    <b-col cols="4">
                        <SearchComponent v-model="selectedLocation" label="Storage Location (Optional)" search-u-r-l="/inventory/inventory-locations" clearable/>
                    </b-col>
                </b-row>
                <b-row class="mt-2">
                    <b-col cols="6">
                        <fg-input v-model.number="packageSize" label="Package Size" type="number" step="0.01" required>
                            <template #label>
                                Package Size <span class="text-danger">*</span>
                                <small v-if="selectedCompound?.packageSize" class="text-muted">
                                    (Compound default: {{ selectedCompound.packageSize }} {{ selectedCompound.unitOfMeasure }})
                                </small>
                            </template>
                        </fg-input>
                    </b-col>
                    <b-col cols="6" class="mt-4">
                        <strong>Labels to Print:</strong> {{ labelCount }}
                        <template v-if="modeInternal === 'prescription' && labelCount > 1">
                            <br><small class="text-muted">(Labels will be numbered: "1 of {{ labelCount }}", "2 of {{ labelCount }}", etc.)</small>
                        </template>
                    </b-col>
                </b-row>
                <b-row v-if="!isLoadingPrinters" class="mt-2">
                    <b-col cols="12">
                        <label>Printer:</label>
                        <b-dropdown :text="prettyPrinterName(activeLocalPrinter)" variant="outline-primary" block>
                            <b-dropdown-item-button v-for="p in localPrinters" :key="p.name"
                                :active="p.name === activeLocalPrinter?.name" @click="activeLocalPrinter = p">
                                <i class="fa fa-print"></i>
                                {{ prettyPrinterName(p) }}
                                <div class="d-flex" style="gap: 6px;">
                                    <small v-if="p.default">Local Default</small>
                                    <small v-if="p.status !== 'Unknown'">{{ p.status }}</small>
                                </div>
                            </b-dropdown-item-button>
                            <b-dropdown-item disabled v-if="!hasLocalPrinters">
                                <i class="fa fa-info-circle" />
                                To print to local printers, install<br>the Scriptly Client Service
                            </b-dropdown-item>
                        </b-dropdown>
                    </b-col>
                </b-row>
                <b-row class="mt-3">
                    <b-col>
                        <b-button variant="success" @click="finalizeBatch" :disabled="!canFinalize || isFinalizing"
                            v-b-tooltip.hover.ds750
                            title="Finalizes the compound batch and prints the compound product labels — one per package, including lot number and beyond-use date.">
                            <b-spinner v-if="isFinalizing" small class="mr-1" />
                            {{ isFinalizing ? 'Finalizing...' : 'Finalize Batch' }}
                        </b-button>
                        <b-button variant="secondary" @click="showCompletionModal = false" class="ml-2">
                            Cancel
                        </b-button>
                    </b-col>
                </b-row>
            </b-modal>
        </div>
    </card>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import SearchComponent from '@/components/SearchComponent.vue';
import { CompoundDrug, CompoundBatch, CompoundBatchSteps, CompoundSteps, CompoundBatchIngredients, CompoundPreset } from '@/models/Drug/CompoundDrug';
import { DrugProduct, DrugSource } from '@/models/Drug/DrugProduct';
import Axios from 'axios';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import { Store } from "@/models/Store";
import { Program } from "@/models/Program";
import { Prescription } from "@/models/Prescription";
import { compareVersions } from "compare-versions";
import { InventoryLocation } from "@/models/InventoryLocation";
import { ASSAY_BASIS_LABELS, AssayBasis } from "@/models/Inventory";
import {
    ActivityFactorResult,
    calculateActivityFactor,
    calculateStepExpectedWeights,
    describeCoaRangeWarnings,
    describeUnusableFixedFactor,
    formatActivityFactor,
    hasAnyCoaValue,
    StepLink,
    toNumberOrNull,
} from "@/util/activityFactor";
import { ACTIVITY_FACTOR_SETTINGS_OFF, ActivityFactorSettings, loadActivityFactorSettings } from "@/util/activityFactorApi";

const minClientVersion = "1.3.0";

/** One inventory row offered for an ingredient, as `available-lots-all-stores` returns it. */
export interface AvailableIngredientLot {
    inventoryId: number;
    lotNumber: string;
    expirationDate: Date | string | null;
    quantity: number;
    storeId: number;
    storeName: string;
    assayPercent: number | null;
    assayBasis: AssayBasis | null;
    lodPercent: number | null;
}

/** A technician's manual weigh-out for one ingredient; who and when are stamped server-side. */
export interface IngredientOverride {
    quantity: number;
    reason: string;
}

/** The scaled ingredient row plus the formula's activity-factor settings for it. */
export interface ScaledIngredientRow {
    compoundIngredientID: number;
    drugID: number;
    drugSource: number;
    name: string;
    isSwapped: boolean;
    formulaQuantity: number;
    scaledQuantity: number;
    unitOfMeasure: string;
    lotNumber: string | null;
    expirationDate: Date | string | null;
    applyActivityFactor: boolean;
    fixedConversionFactor: number | null;
}

/**
 * What the selected lot does to one ingredient's weigh-out: the calculation result, the COA that
 * drove it, and everything the row must say about it. Derived, never stored.
 */
export interface IngredientActivity {
    /** The ingredient takes part: feature on and the formula flags it. */
    flagged: boolean;
    lot: AvailableIngredientLot | null;
    result: ActivityFactorResult;
    override: IngredientOverride | null;
    /** "99.7% Anhydrous · LOD 3.1%" — only for flagged ingredients whose lot carries a COA. */
    coaText: string | null;
    warnings: string[];
}

/** A batch step's link to an ingredient, resolved against the ingredients table as it stands now. */
export interface LinkedStepWeight {
    /** Null once the ingredient has left the formula. */
    ingredient: ScaledIngredientRow | null;
    activity: IngredientActivity | null;
    /** The step's target: its share of the ingredient's current weigh-out; null when it cannot be computed. */
    expectedWeight: number | null;
    /**
     * "50% of the weigh-out" / "the remainder after the other linked steps", or null where the step
     * declares no share and carries the whole weigh-out.
     */
    shareText: string | null;
}

@Component({
    name: "CompoundFillingPage",
    components: {
        SearchComponent
    }
})
export default class CompoundFillingPage extends Vue {
    @Prop() private compoundId?: string;
    @Prop() private mode?: 'batch' | 'prescription';

    private modeInternal: 'batch' | 'prescription' | null = null;
    private compoundSearch: CompoundDrug = new CompoundDrug();
    private selectedCompound: CompoundDrug | null = null;
    private selectedPrescription: Prescription = new Prescription();
    private batchSize: number | null = null;
    private batch: CompoundBatch | null = null;
    private currentStepIndex = 0;
    protected showCompletionModal = false;
    protected isFinalizing = false;
    private lotNumber = '';
    private beyondUseDate = '';
    private packageSize: number | null = null;
    private currentStepPhotoFile: File | null = null;
    protected currentStepPhotoPreview: string | null = null;
    private selectedStore: Store = new Store();
    private selectedProgram: Program | null = new Program();
    private selectedLocation: InventoryLocation | null = new InventoryLocation();
    private isResumingBatch = false;

    // Printer management properties
    protected isLoadingPrinters = false;
    protected localPrinters: any[] = [];
    private activeLocalPrinter: any | null = null;
    protected hasSupportedClientService: boolean | null = null;
    protected labelZpl: any = {};

    // Preset and ingredient swap state
    private presets: CompoundPreset[] = [];
    private selectedPresetId: number | null = null;
    private ingredientSwaps: Record<number, { drugID: number; drugSource: number; drugName: string; quantity?: number; unitOfMeasure?: string }> = {};
    private ingredientAvailableLots: Record<number, AvailableIngredientLot[]> = {};
    private selectedLotIds: Record<number, number | null> = {};

    // Activity factor: the client's settings, and each ingredient's manual override, keyed by
    // compound ingredient id like the lot selection above. Kept out of the scaledIngredients rows,
    // which are rebuilt whenever the formula or batch changes.
    private activityFactorSettings: ActivityFactorSettings = ACTIVITY_FACTOR_SETTINGS_OFF;
    private ingredientOverrides: Record<number, IngredientOverride> = {};
    protected showOverrideModal = false;
    private overrideTargetId: number | null = null;
    protected overrideQuantityInput: number | string | null = null;
    protected overrideReasonInput = '';

    async mounted() {
        loadActivityFactorSettings().then(settings => { this.activityFactorSettings = settings; });

        // Check if resuming an existing batch
        const batchId = this.$route.query.batchId as string;
        if (batchId) {
            await this.resumeExistingBatch(parseInt(batchId));
        } else {
            // Initialize from route params if provided
            if (this.mode) {
                this.modeInternal = this.mode;
            }
            if (this.compoundId) {
                await this.loadCompoundFromRoute(parseInt(this.compoundId));
            }
        }

        // Initialize printer support
        this.getLocalPrinters();
        this.getSupportedClientService();
    }

    get modeTitle() {
        return this.modeInternal === 'batch' ? 'Create Batch for Inventory' : 'Prepare for Prescription';
    }

    async loadCompoundFromRoute(compoundId: number) {
        try {
            await this.loadCompoundDetails(compoundId);
            // loadCompoundDetails already sets selectedCompound with steps loaded
            this.compoundSearch = this.selectedCompound!;
        } catch (error) {
            console.error('Error loading compound from route', error);
            this.$notification(NotificationOptions.error(error));
        }
    }

    async resumeExistingBatch(batchId: number) {
        try {
            this.isResumingBatch = true;

            // Load the batch
            const batchResponse = await Axios.get<CompoundBatch>(`/CompoundBatch/${batchId}`);
            this.batch = batchResponse.data;

            console.log('Resuming batch:', {
                batchID: this.batch.id,
                batchNumber: this.batch.batchNumber,
                batchSize: this.batch.batchSize,
                compoundID: this.batch.compoundID,
                status: this.batch.status,
                rxNumber: this.batch.rxNumber
            });

            // Determine mode based on whether it's a prescription batch
            if (this.batch.rxNumber) {
                this.modeInternal = 'prescription';

                // Load prescription details
                const rxID = `${this.batch.storeId}-${this.batch.rxNumber}-${this.batch.rfNumber || 0}`;
                const prescriptionResponse = await Axios.get<Prescription>(`/api/Prescription/${rxID}`);
                this.selectedPrescription = new Prescription(prescriptionResponse.data);
            } else {
                this.modeInternal = 'batch';
            }

            // Load compound details
            await this.loadCompoundDetails(this.batch.compoundID);
            this.compoundSearch = this.selectedCompound!;
            this.batchSize = this.batch.batchSize;

            // Find the current step (first incomplete step)
            if (this.batch.steps && this.batch.steps.length > 0) {
                const incompleteStepIndex = this.batch.steps.findIndex(step => !step.completed);
                this.currentStepIndex = incompleteStepIndex >= 0 ? incompleteStepIndex : 0;
            }

            this.$notification(NotificationOptions.success(`Resuming batch ${this.batch.batchNumber}`));
            this.loadAvailableLotsForIngredients();
        } catch (error) {
            console.error('Error resuming batch', error);
            this.$notification(NotificationOptions.error('Failed to resume batch. Please try again.'));
            // Redirect back to batch audit page
            this.$router.push({ name: 'CompoundBatchAudit' });
        } finally {
            this.isResumingBatch = false;
        }
    }

    get currentStep(): CompoundBatchSteps | null {
        if (!this.batch?.steps || this.currentStepIndex >= this.batch.steps.length) return null;
        return this.batch.steps[this.currentStepIndex];
    }

    get currentStepConfig(): CompoundSteps | null {
        if (!this.selectedCompound?.steps || !this.currentStep) return null;
        return this.selectedCompound.steps.find(s => s.stepNumber === this.currentStep!.stepNumber) || null;
    }

    /**
     * How a batch step linked to an ingredient gets its target: the ingredient's effective quantity
     * (the Weigh-Out — activity-adjusted, or the technician's override) as it stands right now, times
     * the step's share, or the balance the other linked steps leave. Null for an unlinked step. The
     * expected weight is null when the linked ingredient is no longer in the formula (the batch
     * outlived it) or its scaled quantity cannot be computed — the step then cannot be completed.
     */
    linkedStepWeightFor(step: CompoundBatchSteps | null | undefined): LinkedStepWeight | null {
        if (step?.compoundIngredientID == null) return null;

        const ingredient = this.scaledIngredients.find(row => row.compoundIngredientID === step.compoundIngredientID) ?? null;
        const activity = ingredient ? this.ingredientActivity[ingredient.compoundIngredientID] ?? null : null;
        // A linked step need not declare a share. The remainder step absorbs what the others leave;
        // a step with no proportion recorded carries the whole weigh-out. Neither has a percentage
        // to state, and inventing one -- 0%, or the raw null -- misreads the record.
        const shareText = step.isRemainderStep
            ? 'the remainder after the other linked steps'
            : step.proportionPercent != null
                ? `${step.proportionPercent}% of the weigh-out`
                : null;

        if (!ingredient || !activity) {
            return { ingredient, activity, expectedWeight: null, shareText };
        }

        const siblings = (this.batch?.steps ?? []).filter(s => s.compoundIngredientID === step.compoundIngredientID);
        const links: StepLink[] = siblings.map(s => ({ proportionPercent: s.proportionPercent, isRemainder: !!s.isRemainderStep }));
        const weights = calculateStepExpectedWeights(activity.result.effectiveQuantity, links);
        const index = siblings.indexOf(step);
        const expectedWeight = index >= 0 ? weights[index] : null;

        return { ingredient, activity, expectedWeight, shareText };
    }

    get linkedStepWeight(): LinkedStepWeight | null {
        return this.linkedStepWeightFor(this.currentStep);
    }

    get scaledExpectedWeight(): number | null {
        // A step linked to an ingredient takes its target from that ingredient's weigh-out as it stands
        // now, not from the value seeded when the batch was created (no lot was chosen then).
        const linked = this.linkedStepWeight;
        if (linked) return linked.expectedWeight;

        // The batch step carries the weight the server already scaled to this batch size. Older batches
        // stored NULL, so fall back to scaling the formula step here - never to the unscaled weight,
        // which reads as a valid target and would let an out-of-tolerance step pass.
        if (this.currentStep?.expectedWeight != null) return this.currentStep.expectedWeight;

        const expectedWeight = this.currentStepConfig?.expectedWeight;
        const packageSize = this.selectedCompound?.packageSize;
        if (expectedWeight == null || !packageSize || !this.batch?.batchSize) return null;

        return expectedWeight * (this.batch.batchSize / packageSize);
    }

    /** The unit a step's weight is in: the linked ingredient's, otherwise the compound's. */
    get expectedWeightUnit(): string {
        return this.linkedStepWeight?.ingredient?.unitOfMeasure || this.selectedCompound?.unitOfMeasure || '';
    }

    get weightRange(): { min: number, max: number } | null {
        const scaledExpectedWeight = this.scaledExpectedWeight;
        const weightTolerance = this.currentStepConfig?.weightTolerance;

        // Explicitly check for null/undefined, not falsy (0 could be valid)
        if (scaledExpectedWeight == null || weightTolerance == null) return null;

        const tolerance = weightTolerance / 100;
        return {
            min: scaledExpectedWeight * (1 - tolerance),
            max: scaledExpectedWeight * (1 + tolerance)
        };
    }

    get canProceed(): boolean {
        if (!this.currentStep || !this.currentStepConfig) return false;

        // Check weight requirement
        if (this.currentStepConfig.requireWeight) {
            // Without a range there is nothing to check the weight against. validateWeight only runs on
            // input, so a step navigated back to still carries the verdict it was saved with - trusting
            // that would advance a step the page is telling the technician cannot be completed.
            if (this.weightRange === null) return false;
            if (!this.currentStep.actualWeight) return false;
            if (!this.currentStep.withinTolerance) return false;
        }

        // Check picture requirement
        if (this.currentStepConfig.requirePicture) {
            if (!this.currentStepPhotoFile && !this.currentStep.photoId) return false;
        }

        return true;
    }

    get labelCount(): number {
        if (!this.batch?.batchSize) return 0;

        // For batch mode (inventory), use the package size entered in the modal
        if (this.modeInternal === 'batch') {
            if (!this.packageSize) return 0;
            return Math.ceil(this.batch.batchSize / this.packageSize);
        }

        // For prescription mode, use the compound's package size
        if (this.modeInternal === 'prescription') {
            const compoundPackageSize = this.selectedCompound?.packageSize;
            if (!compoundPackageSize) return 1; // Default to 1 label if package size not set
            return Math.ceil(this.batch.batchSize / compoundPackageSize);
        }

        return 0;
    }

    /**
     * The store the finished product is posted to. It starts as an empty Store, which is truthy, so
     * only an id means one was picked -- an inventory batch has no store of its own to fall back on.
     */
    get hasSelectedStore(): boolean {
        return !!this.selectedStore?.id;
    }

    /** Marks the Store field invalid while it is empty, so a disabled Finalize button explains itself. */
    get storeFieldState(): boolean | null {
        return this.hasSelectedStore ? null : false;
    }

    get canFinalize(): boolean {
        if (this.modeInternal === 'batch') {
            return !!(this.lotNumber && this.beyondUseDate && this.packageSize && this.hasSelectedStore);
        }
        if (this.modeInternal === 'prescription') {
            return !!(this.lotNumber && this.beyondUseDate);
        }
        return false;
    }

    get scaledIngredients(): ScaledIngredientRow[] {
        if (!this.selectedCompound?.ingredients || !this.batch?.batchSize || !this.selectedCompound?.packageSize) {
            console.log('Ingredients not available:', {
                hasIngredients: !!this.selectedCompound?.ingredients,
                ingredientsCount: this.selectedCompound?.ingredients?.length,
                hasBatchSize: !!this.batch?.batchSize,
                batchSize: this.batch?.batchSize,
                hasPackageSize: !!this.selectedCompound?.packageSize,
                packageSize: this.selectedCompound?.packageSize
            });
            return [];
        }

        const scaleFactor = this.batch.batchSize / this.selectedCompound.packageSize;

        const scaled = this.selectedCompound.ingredients.map(ingredient => {
            const swap = this.ingredientSwaps[ingredient.id];
            const effectiveDrugID = swap?.drugID ?? ingredient.drugID;
            const effectiveDrugSource = swap?.drugSource ?? ingredient.drugSource;
            const effectiveName = swap?.drugName ?? ingredient.drugName ?? 'Unknown';

            // Check if batch has ingredient data (when resuming)
            const batchIngredient = this.batch?.ingredients?.find(
                bi => bi.drugID === effectiveDrugID && bi.drugSource === effectiveDrugSource
            );

            return {
                compoundIngredientID: ingredient.id,
                drugID: effectiveDrugID,
                drugSource: effectiveDrugSource,
                name: effectiveName,
                isSwapped: !!swap,
                formulaQuantity: swap?.quantity ?? ingredient.quantity ?? 0,
                scaledQuantity: (swap?.quantity ?? ingredient.quantity ?? 0) * scaleFactor,
                unitOfMeasure: swap?.unitOfMeasure ?? ingredient.unitOfMeasure ?? this.selectedCompound?.unitOfMeasure ?? '',
                lotNumber: batchIngredient?.lotNumber || null,
                expirationDate: batchIngredient?.expirationDate || null,
                // The flag belongs to the ingredient's role in the formula, so a swapped drug keeps
                // it. A fixed conversion factor is a property of the exact substance the formula
                // names (e.g. the hemihydrate correction), so it does not follow a swap: the
                // substitute's weigh-out comes from its own lot's COA.
                applyActivityFactor: !!ingredient.applyActivityFactor,
                fixedConversionFactor: swap ? null : toNumberOrNull(ingredient.fixedConversionFactor),
            };
        });

        console.log('Scaled ingredients:', scaled);
        return scaled;
    }

    get activityFactorEnabled(): boolean {
        return this.activityFactorSettings.enabled;
    }

    /** Ingredient quantities show at the configured precision once the feature is on; two places otherwise, as before. */
    get quantityDecimals(): number {
        return this.activityFactorEnabled ? this.activityFactorSettings.decimalPlaces : 2;
    }

    /**
     * Activity-factor view of every ingredient row, keyed by compound ingredient id: the selected
     * lot's COA run through the same calculation the server applies, the override if any, and the
     * warnings the row must show. Unflagged ingredients pass through at factor 1.0 and never warn.
     */
    get ingredientActivity(): Record<number, IngredientActivity> {
        const settings = this.activityFactorSettings;
        const view: Record<number, IngredientActivity> = {};

        for (const row of this.scaledIngredients) {
            const lot = this.selectedLotFor(row.compoundIngredientID);
            const flagged = settings.enabled && row.applyActivityFactor;
            const override = this.ingredientOverrides[row.compoundIngredientID] ?? null;

            const result = calculateActivityFactor({
                scaledQuantity: row.scaledQuantity,
                applyActivityFactor: flagged,
                fixedConversionFactor: row.fixedConversionFactor,
                assayPercent: lot?.assayPercent,
                assayBasis: lot?.assayBasis,
                lodPercent: lot?.lodPercent,
                overrideQuantity: override?.quantity ?? null,
            }, settings.bounds);

            const warnings: string[] = [];
            if (flagged) {
                switch (result.fallback) {
                    case 'NoCoa':
                        warnings.push(lot
                            ? 'This lot has no COA — factor 1.0 applied; the batch record will carry a missing-COA flag.'
                            : 'No lot selected — factor 1.0 applies until a lot with a COA is chosen; saved like this, the ingredient carries a missing-COA flag.');
                        break;
                    case 'UnusableCoa':
                        warnings.push('This lot\'s COA cannot yield a valid factor — treated as no COA (factor 1.0).');
                        break;
                    case 'UnusableFixedFactor':
                        warnings.push(describeUnusableFixedFactor());
                        break;
                }
                if (result.lodAssumedZero) {
                    warnings.push('LOD / water content is blank on this lot, so it is treated as 0% for its basis.');
                }
                warnings.push(...describeCoaRangeWarnings(result.rangeWarnings, settings.bounds));
            }

            view[row.compoundIngredientID] = {
                flagged,
                lot,
                result,
                override,
                coaText: flagged && lot && hasAnyCoaValue(lot) ? this.describeCoa(lot) : null,
                warnings,
            };
        }

        return view;
    }

    /** True once any ingredient carries a manual weigh-out; such a batch is routed for pharmacist sign-off. */
    get hasOverrides(): boolean {
        return Object.values(this.ingredientOverrides).some(override => !!override);
    }

    /** True while a flagged ingredient is being filled from a lot with no usable COA (factor 1.0 applied); such a batch is routed for pharmacist sign-off. */
    get hasMissingCoa(): boolean {
        return this.activityFactorEnabled
            && Object.values(this.ingredientActivity).some(activity => activity.result.missingCoa);
    }

    /**
     * Why the batch will be held for pharmacist verification regardless of the pharmacy's setting —
     * one sentence per review flag the record will carry (the server derives the same flags from the
     * saved rows). Empty when nothing on the record needs review.
     */
    get reviewHoldReasons(): string[] {
        const reasons: string[] = [];
        if (this.hasOverrides) reasons.push('One or more weigh-outs were overridden by hand.');
        if (this.hasMissingCoa) reasons.push('One or more flagged ingredients were filled from a lot with no usable COA.');
        return reasons;
    }

    get overrideTarget(): ScaledIngredientRow | null {
        if (this.overrideTargetId === null) return null;
        return this.scaledIngredients.find(row => row.compoundIngredientID === this.overrideTargetId) ?? null;
    }

    get overrideTargetActivity(): IngredientActivity | null {
        return this.overrideTargetId === null ? null : this.ingredientActivity[this.overrideTargetId] ?? null;
    }

    /** An override needs a positive quantity and a reason before it can be applied. */
    get canApplyOverride(): boolean {
        const quantity = toNumberOrNull(this.overrideQuantityInput);
        return quantity !== null && quantity > 0 && this.overrideReasonInput.trim().length > 0;
    }

    selectedLotFor(compoundIngredientID: number): AvailableIngredientLot | null {
        const inventoryId = this.selectedLotIds[compoundIngredientID];
        if (!inventoryId) return null;
        const lots = this.ingredientAvailableLots[compoundIngredientID] || [];
        return lots.find(lot => lot.inventoryId === inventoryId) ?? null;
    }

    activityFor(ingredient: ScaledIngredientRow): IngredientActivity | null {
        return this.ingredientActivity[ingredient.compoundIngredientID] ?? null;
    }

    describeCoa(lot: AvailableIngredientLot): string {
        const parts: string[] = [];
        if (lot.assayPercent != null) parts.push(`${lot.assayPercent}%`);
        if (lot.assayBasis && lot.assayBasis in ASSAY_BASIS_LABELS) parts.push(ASSAY_BASIS_LABELS[lot.assayBasis]);
        let text = parts.join(' ');
        if (lot.lodPercent != null) text += `${text ? ' · ' : ''}LOD ${lot.lodPercent}%`;
        return text;
    }

    formatQty(value: number | null | undefined): string {
        if (value === null || value === undefined || !Number.isFinite(value)) return '';
        return value.toFixed(this.quantityDecimals);
    }

    formatFactor(factor: number | null | undefined): string {
        return formatActivityFactor(factor, this.activityFactorSettings.decimalPlaces);
    }

    openOverride(ingredient: ScaledIngredientRow) {
        const existing = this.ingredientOverrides[ingredient.compoundIngredientID];
        this.overrideTargetId = ingredient.compoundIngredientID;
        this.overrideQuantityInput = existing?.quantity ?? null;
        this.overrideReasonInput = existing?.reason ?? '';
        this.showOverrideModal = true;
    }

    applyOverride() {
        if (this.overrideTargetId === null || !this.canApplyOverride) return;
        this.$set(this.ingredientOverrides, this.overrideTargetId, {
            quantity: toNumberOrNull(this.overrideQuantityInput)!,
            reason: this.overrideReasonInput.trim(),
        });
        this.showOverrideModal = false;
    }

    clearOverride(compoundIngredientID: number | null = this.overrideTargetId) {
        if (compoundIngredientID === null) return;
        this.$delete(this.ingredientOverrides, compoundIngredientID);
        this.showOverrideModal = false;
    }

    /**
     * The ingredient row as the batch record stores it. With the feature off this is the payload
     * the page always sent, plus the selected inventory row so completion deducts from that lot.
     * With it on, `quantity` is the effective weigh-out (override, else adjusted) and the full
     * snapshot travels with it; the COA is captured for flagged ingredients even when a fixed
     * factor replaced it, so the record shows what was ignored. Unflagged ingredients carry no
     * factor or COA — nothing was applied to them.
     */
    buildIngredientPayload(ingredient: ScaledIngredientRow) {
        const inventoryId = this.selectedLotIds[ingredient.compoundIngredientID] ?? null;
        const base = {
            // The formula line, which survives a preset swap where drugID does not. The server
            // derives the missing-COA review flag from it rather than from what we report.
            compoundIngredientId: ingredient.compoundIngredientID,
            drugID: ingredient.drugID,
            drugSource: ingredient.drugSource,
            unitOfMeasure: ingredient.unitOfMeasure,
            lotNumber: ingredient.lotNumber || null,
            expirationDate: ingredient.expirationDate || null,
            inventoryId,
        };

        if (!this.activityFactorEnabled) {
            return { ...base, quantity: ingredient.scaledQuantity };
        }

        const activity = this.ingredientActivity[ingredient.compoundIngredientID];
        const result = activity.result;
        const snapshotCoa = activity.flagged && activity.lot;

        return {
            ...base,
            quantity: result.effectiveQuantity,
            scaledQuantity: result.scaledQuantity,
            adjustedQuantity: result.adjustedQuantity,
            activityFactor: activity.flagged ? result.activityFactor : null,
            assayPercent: snapshotCoa ? activity.lot!.assayPercent : null,
            assayBasis: snapshotCoa ? activity.lot!.assayBasis : null,
            lodPercent: snapshotCoa ? activity.lot!.lodPercent : null,
            fixedConversionFactor: result.factorSource === 'FixedFactor' ? ingredient.fixedConversionFactor : null,
            overrideQuantity: activity.override?.quantity ?? null,
            overrideReason: activity.override?.reason ?? null,
            missingCoaFlag: result.missingCoa,
        };
    }

    /**
     * A resumed batch may already hold ingredient rows — a finalize that failed after saving them
     * (a short lot, say) leaves the record behind. Put the lot the technician chose and any override
     * back on the rows so a retry weighs against the same numbers. A lot no longer offered stays
     * unselected; its lot number is still on the row as free text.
     */
    restorePersistedSelections() {
        const persisted = this.batch?.ingredients;
        if (!persisted?.length) return;

        for (const row of this.scaledIngredients) {
            const saved = persisted.find(bi => bi.drugID === row.drugID && bi.drugSource === row.drugSource);
            if (!saved) continue;

            const lots = this.ingredientAvailableLots[row.compoundIngredientID] || [];
            if (saved.inventoryId && lots.some(lot => lot.inventoryId === saved.inventoryId)) {
                this.$set(this.selectedLotIds, row.compoundIngredientID, saved.inventoryId);
            }
            if (saved.overrideQuantity != null) {
                this.$set(this.ingredientOverrides, row.compoundIngredientID, {
                    quantity: saved.overrideQuantity,
                    reason: saved.overrideReason ?? '',
                });
            }
        }
    }

    selectMode(mode: 'batch' | 'prescription') {
        this.modeInternal = mode;
    }

    onCompoundSelected(compound: CompoundDrug) {
        this.selectedCompound = compound;
        this.loadCompoundDetails(compound.compoundID!);
    }

    async loadCompoundDetails(compoundId: number) {
        try {
            const response = await Axios.get<CompoundDrug>(`/Compound/${compoundId}`);
            this.selectedCompound = response.data;

            console.log('Loaded compound:', {
                compoundID: this.selectedCompound.compoundID,
                name: this.selectedCompound.name,
                packageSize: this.selectedCompound.packageSize,
                hasIngredients: !!this.selectedCompound.ingredients,
                ingredientsCount: this.selectedCompound.ingredients?.length || 0,
                ingredients: this.selectedCompound.ingredients
            });

            // Load steps
            const stepsResponse = await Axios.get<CompoundSteps[]>(`/Compound/${compoundId}/steps`);
            if (this.selectedCompound) {
                this.selectedCompound.steps = stepsResponse.data;
            }

            // Load drug names for ingredients
            await this.loadIngredientDrugNames();

            // Load presets for ingredient swapping
            await this.loadPresetsAndEquivalents(compoundId);
        } catch (error) {
            console.error('Error loading compound details', error);
            this.$notification(NotificationOptions.error(error));
        }
    }

    async loadPresetsAndEquivalents(compoundId: number) {
        this.presets = [];
        this.selectedPresetId = null;
        this.ingredientSwaps = {};

        try {
            const presetsResponse = await Axios.get<CompoundPreset[]>(`/Compound/${compoundId}/presets`);
            this.presets = presetsResponse.data;
        } catch (error) {
            console.warn('Error loading presets:', error);
        }
    }

    async applyPreset() {
        this.ingredientSwaps = {};

        if (this.selectedPresetId === null) return;

        const preset = this.presets.find(p => p.id === this.selectedPresetId);
        if (!preset?.ingredients) return;

        const nameFetches: Promise<void>[] = [];

        for (const presetIngredient of preset.ingredients) {
            const originalIngredient = this.selectedCompound?.ingredients?.find(
                i => i.id === presetIngredient.compoundIngredientID
            );
            if (!originalIngredient) continue;

            // Only add to swaps if it's different from the original
            if (presetIngredient.drugID !== originalIngredient.drugID ||
                presetIngredient.drugSource !== originalIngredient.drugSource) {
                this.$set(this.ingredientSwaps, presetIngredient.compoundIngredientID, {
                    drugID: presetIngredient.drugID,
                    drugSource: presetIngredient.drugSource,
                    drugName: presetIngredient.drugName || 'Loading...',
                    quantity: presetIngredient.quantity,
                    unitOfMeasure: presetIngredient.unitOfMeasure
                });

                // Fetch drug name if not available
                if (!presetIngredient.drugName) {
                    const ingredientId = presetIngredient.compoundIngredientID;
                    nameFetches.push(
                        Axios.get(`/api/Drug/${presetIngredient.drugID}`, {
                            params: { drugSource: presetIngredient.drugSource }
                        }).then(response => {
                            const swap = this.ingredientSwaps[ingredientId];
                            if (swap && swap.drugID === presetIngredient.drugID) {
                                this.$set(this.ingredientSwaps, ingredientId, {
                                    ...swap,
                                    drugName: response.data?.productNameShort || response.data?.name || 'Unknown'
                                });
                            }
                        }).catch(() => {
                            const swap = this.ingredientSwaps[ingredientId];
                            if (swap && swap.drugID === presetIngredient.drugID) {
                                this.$set(this.ingredientSwaps, ingredientId, { ...swap, drugName: 'Unknown' });
                            }
                        })
                    );
                }
            }
        }

        if (nameFetches.length > 0) {
            await Promise.all(nameFetches);
        }

        // Reload lots for the swapped drugs
        this.loadAvailableLotsForIngredients();
    }

    async loadAvailableLotsForIngredients() {
        if (!this.selectedCompound?.ingredients) return;

        this.ingredientAvailableLots = {};
        this.selectedLotIds = {};
        // An override was made against a specific drug's computed weigh-out; like the lot selection
        // it does not survive the formula changing under it (a preset swap reloads the lots).
        this.ingredientOverrides = {};

        const items = this.selectedCompound.ingredients.map(ingredient => {
            const swap = this.ingredientSwaps[ingredient.id];
            return {
                DrugSource: swap?.drugSource ?? ingredient.drugSource,
                PackageId: swap?.drugID ?? ingredient.drugID
            };
        });

        try {
            const response = await Axios.post<Record<string, AvailableIngredientLot[]>>('/Print/available-lots-all-stores', { items });
            const lotsByKey = response.data;

            for (const ingredient of this.selectedCompound.ingredients) {
                const swap = this.ingredientSwaps[ingredient.id];
                const drugSource = swap?.drugSource ?? ingredient.drugSource;
                const drugID = swap?.drugID ?? ingredient.drugID;
                const key = `${drugSource}_${drugID}`;
                this.$set(this.ingredientAvailableLots, ingredient.id, lotsByKey[key] || []);
                this.$set(this.selectedLotIds, ingredient.id, null);
            }

            this.restorePersistedSelections();
        } catch (error) {
            console.warn('Error loading available lots:', error);
        }
    }

    getIngredientLots(ingredient: ScaledIngredientRow): AvailableIngredientLot[] {
        return this.ingredientAvailableLots[ingredient.compoundIngredientID] || [];
    }

    buildIngredientLotOptions(ingredient: ScaledIngredientRow) {
        const lots = this.getIngredientLots(ingredient);
        const options: Array<{ value: number | null; text: string }> = [
            { value: null, text: '-- Manual Entry --' }
        ];
        for (const lot of lots) {
            const exp = lot.expirationDate ? new Date(lot.expirationDate).toLocaleDateString() : 'N/A';
            options.push({
                value: lot.inventoryId,
                text: `${lot.lotNumber} (Exp: ${exp}, Qty: ${lot.quantity}) - ${lot.storeName || 'Store ' + lot.storeId}`
            });
        }
        return options;
    }

    onLotSelectedForIngredient(ingredient: ScaledIngredientRow, inventoryId: number | null) {
        this.$set(this.selectedLotIds, ingredient.compoundIngredientID, inventoryId);
        if (!inventoryId) {
            ingredient.lotNumber = null;
            ingredient.expirationDate = null;
            return;
        }
        const lots = this.getIngredientLots(ingredient);
        const lot = lots.find(l => l.inventoryId === inventoryId);
        if (lot) {
            ingredient.lotNumber = lot.lotNumber || '';
            ingredient.expirationDate = lot.expirationDate ? new Date(lot.expirationDate).toISOString().split('T')[0] : null;
        }
    }

    get hasIngredientFromDifferentStore(): boolean {
        const currentStoreId = this.selectedStore?.id || this.batch?.storeId;
        if (!currentStoreId) return false;

        for (const ingredientId of Object.keys(this.selectedLotIds)) {
            const lotId = this.selectedLotIds[Number(ingredientId)];
            if (!lotId) continue;
            const lots = this.ingredientAvailableLots[Number(ingredientId)] || [];
            const lot = lots.find(l => l.inventoryId === lotId);
            if (lot && lot.storeId !== currentStoreId) return true;
        }
        return false;
    }

    async loadIngredientDrugNames() {
        if (!this.selectedCompound?.ingredients) return;

        const loadPromises = this.selectedCompound.ingredients.map(async (ingredient) => {
            try {
                const response = await Axios.get<DrugProduct>(`/api/Drug/${ingredient.drugID}`, {
                    params: { drugSource: ingredient.drugSource }
                });
                ingredient.drugName = response.data.productNameShort || 'Unknown Drug';
            } catch (error) {
                console.warn(`Error loading drug name for ingredient ${ingredient.drugID}:`, error);
                ingredient.drugName = 'Error Loading Name';
            }
        });

        await Promise.all(loadPromises);
    }

    async startBatch() {
        if (!this.selectedCompound || !this.batchSize) return;

        try {
            const batchData = {
                compoundID: this.selectedCompound.compoundID,
                batchSize: this.batchSize,
                storeId: null,
                rxNumber: null,
                rfNumber: null
            };

            const response = await Axios.post<CompoundBatch>('/CompoundBatch', batchData);
            this.batch = response.data;

            console.log('Batch created:', {
                batchID: this.batch.id,
                batchNumber: this.batch.batchNumber,
                batchSize: this.batch.batchSize,
                compoundID: this.batch.compoundID,
                fullBatch: this.batch
            });

            this.currentStepIndex = 0;
            this.$notification(NotificationOptions.success('Batch started successfully'));
            this.loadAvailableLotsForIngredients();
        } catch (error) {
            console.error('Error starting batch', error);
            this.$notification(NotificationOptions.error(error));
        }
    }

    async onPrescriptionSelected(prescription: Prescription) {
        if (!prescription || !prescription.rxNumber) return;

        try {
            // Load full prescription details using rxID format (storeID-rxNumber-rfNumber)
            const rxID = Prescription.createRxId(prescription.storeID, prescription.rxNumber, prescription.rfNumber);
            const response = await Axios.get<Prescription>(`/api/Prescription/${rxID}`);
            this.selectedPrescription = new Prescription(response.data);

            // Load the compound from the prescription's drug
            // DrugSource.Compound = 2
            if (this.selectedPrescription.drugSource === 2) {
                await this.loadCompoundDetails(this.selectedPrescription.packageID);
                this.batchSize = this.selectedPrescription.dispensed;
            } else {
                this.$notification(NotificationOptions.warning('Selected prescription is not for a compound drug'));
                this.selectedPrescription = new Prescription();
            }
        } catch (error) {
            console.error('Error loading prescription details', error);
            this.$notification(NotificationOptions.error(error));
        }
    }

    async startBatchForPrescription() {
        if (!this.selectedCompound || !this.batchSize || !this.selectedPrescription.rxNumber) return;

        try {
            const batchData = {
                compoundID: this.selectedCompound.compoundID,
                batchSize: this.batchSize,
                storeId: this.selectedPrescription.storeID,
                rxNumber: this.selectedPrescription.rxNumber,
                rfNumber: this.selectedPrescription.rfNumber || 0
            };

            const response = await Axios.post<CompoundBatch>('/CompoundBatch', batchData);
            this.batch = response.data;

            console.log('Prescription batch created:', {
                batchID: this.batch.id,
                batchNumber: this.batch.batchNumber,
                batchSize: this.batch.batchSize,
                compoundID: this.batch.compoundID,
                rxID: `${this.selectedPrescription.storeID}-${this.selectedPrescription.rxNumber}-${this.selectedPrescription.rfNumber || 0}`,
                fullBatch: this.batch
            });

            this.currentStepIndex = 0;
            this.$notification(NotificationOptions.success('Prescription compound preparation started'));
            this.loadAvailableLotsForIngredients();
        } catch (error) {
            console.error('Error starting prescription batch', error);
            this.$notification(NotificationOptions.error(error));
        }
    }

    validateWeight() {
        if (!this.currentStep) return;

        const range = this.weightRange;
        if (!this.currentStep.actualWeight || range === null) {
            this.currentStep.withinTolerance = null;
            return;
        }

        const actual = this.currentStep.actualWeight;
        this.currentStep.withinTolerance = actual >= range.min && actual <= range.max;
    }

    /**
     * A linked step's target moves with its ingredient — choosing a lot with a COA or entering an
     * override changes the weigh-out — so a weight already typed is re-checked against the new range
     * rather than keeping a verdict reached against the old one.
     */
    @Watch('weightRange')
    onWeightRangeChanged() {
        this.validateWeight();
    }

    /** "Lot ABC123 (Main Store)" for the ingredient a linked step draws from; the step has no lot of its own. */
    describeStepLot(linked: LinkedStepWeight | null): string {
        if (!linked?.ingredient) return '';
        const lot = linked.activity?.lot;
        if (lot) return `Lot ${lot.lotNumber}${lot.storeName ? ` (${lot.storeName})` : ''}`;
        if (linked.ingredient.lotNumber) return `Lot ${linked.ingredient.lotNumber} (entered manually)`;
        return 'No lot selected for this ingredient yet';
    }

    onPhotoSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files[0]) {
            this.currentStepPhotoFile = input.files[0];

            // Create preview
            const reader = new FileReader();
            reader.onload = (e) => {
                this.currentStepPhotoPreview = e.target?.result as string;
            };
            reader.readAsDataURL(this.currentStepPhotoFile);
        }
    }

    clearPhoto() {
        this.currentStepPhotoFile = null;
        this.currentStepPhotoPreview = null;
        if (this.$refs.photoInput) {
            (this.$refs.photoInput as HTMLInputElement).value = '';
        }
    }

    async saveCurrentStep() {
        if (!this.batch || !this.currentStep) return;

        try {
            // Upload photo if present
            let photoId = this.currentStep.photoId;
            if (this.currentStepPhotoFile) {
                photoId = await this.uploadPhoto(this.currentStepPhotoFile);
            }

            // Save step data. The expected weight travels with it so the record keeps the target the
            // step was actually weighed against — for a linked step that is derived from the
            // ingredient's weigh-out as it stands now, which the value seeded at batch creation cannot know.
            const stepData = {
                stepNumber: this.currentStep.stepNumber,
                actualWeight: this.currentStep.actualWeight,
                withinTolerance: this.currentStep.withinTolerance,
                photoId: photoId,
                expectedWeight: this.scaledExpectedWeight,
            };

            await Axios.post(`/CompoundBatch/${this.batch.id}/step`, stepData);
        } catch (error) {
            console.error('Error saving step', error);
            this.$notification(NotificationOptions.error(error));
            throw error;
        }
    }

    async uploadPhoto(file: File): Promise<number> {
        const formData = new FormData();
        formData.append('files', file);

        try {
            // Create a new axios instance with baseURL but without the global application/json header
            // This allows multipart/form-data to be set correctly with boundary
            const axiosInstance = Axios.create({
                baseURL: Axios.defaults.baseURL
            });
            delete axiosInstance.defaults.headers.post['Content-Type'];

            const response = await axiosInstance.post<number[]>('/Image/SaveDocument?bypassIngestion=true', formData);

            if (!response.data || response.data.length === 0) {
                throw new Error('No image ID returned from upload');
            }

            return response.data[0];
        } catch (error) {
            console.error('Error uploading photo:', error);
            throw error;
        }
    }

    async saveAllIngredients() {
        if (!this.batch) return;

        try {
            const savePromises = this.scaledIngredients
                .map(ingredient => Axios.post(`/CompoundBatch/${this.batch!.id}/ingredient`, this.buildIngredientPayload(ingredient)));

            await Promise.all(savePromises);
        } catch (error) {
            console.error('Error saving ingredients:', error);
            this.$notification(NotificationOptions.error(error));
            throw error;
        }
    }

    async nextStep() {
        await this.saveCurrentStep();
        this.currentStepIndex++;
        this.currentStepPhotoFile = null;
        this.currentStepPhotoPreview = null;
    }

    async previousStep() {
        if (this.currentStepIndex > 0) {
            this.currentStepIndex--;
            this.currentStepPhotoFile = null;
            this.currentStepPhotoPreview = null;
        }
    }

    async completeBatch() {
        await this.saveCurrentStep();

        // Prepopulate store if in batch mode
        if (this.modeInternal === 'batch' && this.batch?.storeId) {
            try {
                const storeResponse = await Axios.get(`/Store/${this.batch.storeId}`);
                this.selectedStore = storeResponse.data;
            } catch (error) {
                console.warn('Error loading batch store:', error);
            }
        }

        // Prepopulate package size from the compound definition (for both batch and prescription modes)
        if (!this.packageSize && this.selectedCompound?.packageSize) {
            this.packageSize = this.selectedCompound.packageSize;
        }

        this.showCompletionModal = true;
    }

    /**
     * Picks the Store field's value when there is only one place the batch can be made: exactly one
     * active store. Leaves a store already chosen -- a batch sent back for rework reopens with its own.
     */
    autoselectStore(stores: Store[]): Store | undefined {
        if (this.hasSelectedStore) return undefined;
        const activeStores = stores.filter(s => s.active);
        return activeStores.length === 1 ? activeStores[0] : undefined;
    }

    async finalizeBatch() {
        if (!this.batch || this.isFinalizing) return;

        this.isFinalizing = true;

        try {
            // Save all ingredients first
            await this.saveAllIngredients();

            const completionData = {
                lotNumber: this.lotNumber,
                beyondUseDate: this.beyondUseDate ? new Date(this.beyondUseDate) : null,
                packageSize: this.packageSize,
                storeId: this.selectedStore?.id || this.batch.storeId,
                programId: this.selectedProgram?.id || null,
                locationId: this.selectedLocation?.id || null
            };

            const completeResponse = await Axios.post(`/CompoundBatch/${this.batch.id}/complete`, completionData);
            const completedBatch = completeResponse.data;

            // Check if batch went to AwaitingVerification — required by configuration, or because the
            // record carries review flags (an overridden weigh-out, a lot with no COA) that need a
            // pharmacist's acknowledgement.
            if (completedBatch && completedBatch.status === 3) {
                const reason = this.reviewHoldReasons.length > 0 ? `${this.reviewHoldReasons.join(' ')} ` : '';
                this.$notification(NotificationOptions.success(`${reason}Batch submitted for pharmacist verification. Inventory will be posted after verification is complete.`));
                this.$router.push({ name: 'Compound-Drug', params: { id: this.selectedCompound!.compoundID!.toString() } });
                return;
            }

            // Print labels for batch using client service
            if (this.modeInternal === 'batch') {
                try {
                    // Get ZPL data from the API
                    const zplRequest = {
                        storeId: this.selectedStore?.id || this.batch.storeId,
                        labelCount: this.labelCount
                    };
                    const zplResponse = await Axios.post(`/CompoundBatch/${this.batch.id}/label-zpl`, zplRequest);
                    this.labelZpl = zplResponse.data;

                    // Print labels using client service (no numbering for inventory batches)
                    await this.sendLabelZplToClientService(false);
                    this.$notification(NotificationOptions.success(`Batch completed successfully! ${this.labelCount} label(s) sent to printer.`));
                } catch (labelError) {
                    console.error('Error printing batch labels', labelError);
                    this.$notification(NotificationOptions.warning('Batch completed but label printing failed. Please print labels manually.'));
                }
            } else if (this.modeInternal === 'prescription') {
                try {
                    // Get ZPL data from the API for prescription
                    const zplRequest = {
                        storeId: this.batch.storeId,
                        labelCount: this.labelCount
                    };
                    const zplResponse = await Axios.post(`/CompoundBatch/${this.batch.id}/label-zpl`, zplRequest);
                    this.labelZpl = zplResponse.data;

                    // Print labels using client service (with numbering only if multiple labels)
                    const applyNumbering = this.labelCount > 1;
                    await this.sendLabelZplToClientService(applyNumbering);
                    this.$notification(NotificationOptions.success(`Batch completed successfully! ${this.labelCount} label(s) sent to printer.`));
                } catch (labelError) {
                    console.error('Error printing batch labels', labelError);
                    this.$notification(NotificationOptions.warning('Batch completed but label printing failed. Please print labels manually.'));
                }
            } else {
                this.$notification(NotificationOptions.success('Batch completed successfully!'));
            }

            // Redirect based on mode
            if (this.modeInternal === 'prescription' && this.selectedPrescription.rxNumber) {
                // For prescription mode, redirect to prescription details
                this.$router.push({
                    name: 'Prescription',
                    params: {
                        storeID: this.selectedPrescription.storeID.toString(),
                        rxNumber: this.selectedPrescription.rxNumber.toString(),
                        rfNumber: (this.selectedPrescription.rfNumber || 0).toString()
                    }
                });
            } else {
                // For batch mode, redirect to compound details
                this.$router.push({ name: 'Compound-Drug', params: { id: this.selectedCompound!.compoundID!.toString() } });
            }
        } catch (error) {
            console.error('Error completing batch', error);
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.isFinalizing = false;
        }
    }

    async sendLabelZplToClientService(applyNumbering: boolean = false): Promise<boolean> {
        if (!this.labelZpl) {
            this.$notification(NotificationOptions.error("No label data to print"));
            return false;
        }

        try {
            // Try to use WebSocket connection via shared worker if available
            const workerPort = (this as any).$workerPort;
            if (workerPort) {
                // Print warnings via WebSocket
                if (this.labelZpl.warnings?.length) {
                    const warningsRequest = this.buildPrinterRequest(
                        this.labelZpl.warnings.join(""),
                        `${this.batch?.batchNumber}-warnings`
                    );
                    workerPort.postMessage(["clientServicePrintRaw", warningsRequest]);
                    await new Promise(resolve => setTimeout(resolve, 100));
                }

                // Print labels via WebSocket
                for (let i = 1; i <= this.labelCount; i++) {
                    // Only apply numbering if requested (for prescriptions, not inventory batches)
                    const modifiedZpl = applyNumbering
                        ? this.processLabelNumbering(this.labelZpl.primary, i, this.labelCount)
                        : this.labelZpl.primary;
                    const labelRequest = this.buildPrinterRequest(
                        modifiedZpl,
                        `${this.batch?.batchNumber}-label-${i}`
                    );
                    workerPort.postMessage(["clientServicePrintRaw", labelRequest]);
                    await new Promise(resolve => setTimeout(resolve, 100));
                }

                return true;
            }

            // Fallback to HTTP if WebSocket not available
            const printerEndpoint = "http://localhost:7274/print/raw";

            if (this.labelZpl.warnings?.length) {
                const warningsRequest = this.buildPrinterRequest(
                    this.labelZpl.warnings.join(""),
                    `${this.batch?.batchNumber}-warnings`
                );
                await Axios.post(printerEndpoint, warningsRequest);
            }

            for (let i = 1; i <= this.labelCount; i++) {
                // Only apply numbering if requested (for prescriptions, not inventory batches)
                const modifiedZpl = applyNumbering
                    ? this.processLabelNumbering(this.labelZpl.primary, i, this.labelCount)
                    : this.labelZpl.primary;
                const labelRequest = this.buildPrinterRequest(
                    modifiedZpl,
                    `${this.batch?.batchNumber}-label-${i}`
                );
                await Axios.post(printerEndpoint, labelRequest);
            }

            return true;
        } catch (err) {
            console.error(err);
            this.$notification(NotificationOptions.error(err));
            return false;
        }
    }

    buildPrinterRequest(rawContent: string, documentName: string) {
        return {
            printerName: this.activeLocalPrinter?.name,
            rawContent: rawContent,
            documentName: documentName,
            copies: 1
        };
    }

    processLabelNumbering(zplContent: string, currentLabel: number, totalLabels: number): string {
        if (totalLabels > 1) {
            return zplContent.replace('{{LabelCount}}', `${currentLabel} of ${totalLabels}`);
        } else {
            return zplContent.replace('{{LabelCount}}', '');
        }
    }

    async getSupportedClientService() {
        try {
            // Try to use WebSocket connection via shared worker if available
            const workerPort = (this as any).$workerPort;
            if (workerPort) {
                // Request version through the shared worker
                const version = await new Promise<string | null>((resolve) => {
                    const timeout = setTimeout(() => {
                        console.log("WebSocket version request timed out, falling back to HTTP");
                        resolve(null);
                    }, 3000);

                    const handleMessage = (e: MessageEvent) => {
                        if (e.data[0] === "clientServiceVersion") {
                            clearTimeout(timeout);
                            workerPort.removeEventListener("message", handleMessage);
                            resolve(e.data[1] || null);
                        }
                    };

                    workerPort.addEventListener("message", handleMessage);
                    // The version should already be cached, but we can request a refresh
                    workerPort.postMessage(["clientServiceRefreshVersion", null]);
                });

                if (version) {
                    this.hasSupportedClientService = compareVersions(version, minClientVersion) >= 0;
                    return;
                }
            }

            // Fallback to HTTP if WebSocket not available or failed
            const response = await this.$http.get("http://localhost:7274/version");
            const version = response.data;

            this.hasSupportedClientService = compareVersions(version, minClientVersion) >= 0;
        } catch {
            this.hasSupportedClientService = false;
        }
    }

    async getLocalPrinters() {
        try {
            this.isLoadingPrinters = true;

            // Try to use WebSocket connection via shared worker if available
            const workerPort = (this as any).$workerPort;
            if (workerPort) {
                // Request printers through the shared worker
                const printers = await new Promise<any[]>((resolve) => {
                    const timeout = setTimeout(() => {
                        console.log("WebSocket printer request timed out, falling back to HTTP");
                        resolve([]);
                    }, 3000);

                    const handleMessage = (e: MessageEvent) => {
                        if (e.data[0] === "clientServicePrinters") {
                            clearTimeout(timeout);
                            workerPort.removeEventListener("message", handleMessage);
                            resolve(e.data[1] || []);
                        }
                    };

                    workerPort.addEventListener("message", handleMessage);
                    workerPort.postMessage(["clientServiceRefreshPrinters", null]);
                });

                if (printers.length > 0) {
                    this.localPrinters = printers;

                    this.localPrinters.sort((a, b) => {
                        const aHasZPL = a.name.toLowerCase().includes('zpl') || a.name.toLowerCase().includes('zebra');
                        const bHasZPL = b.name.toLowerCase().includes('zpl') || b.name.toLowerCase().includes('zebra');

                        if (aHasZPL && !bHasZPL) return -1;
                        if (!aHasZPL && bHasZPL) return 1;

                        return a.name.localeCompare(b.name);
                    });

                    this.handleDefaultPrinter();
                    return;
                }
            }

            // Fallback to HTTP if WebSocket not available or no printers received
            const response = await this.$http.get("http://localhost:7274/print/devices");
            this.localPrinters = response.data;

            this.localPrinters.sort((a, b) => {
                const aHasZPL = a.name.toLowerCase().includes('zpl') || a.name.toLowerCase().includes('zebra');
                const bHasZPL = b.name.toLowerCase().includes('zpl') || b.name.toLowerCase().includes('zebra');

                if (aHasZPL && !bHasZPL) return -1;
                if (!aHasZPL && bHasZPL) return 1;

                return a.name.localeCompare(b.name);
            });

            this.handleDefaultPrinter();
        } catch {
            //ignore
        } finally {
            this.isLoadingPrinters = false;
        }
    }

    handleDefaultPrinter() {
        const lastPrinter = localStorage.getItem("active-compound-batch-printer");
        if (lastPrinter) {
            this.activeLocalPrinter = this.localPrinters?.find(p => p.name === lastPrinter);
            return;
        }

        if (this.localPrinters.length) {
            this.activeLocalPrinter = this.localPrinters[0];
        }
    }

    @Watch('activeLocalPrinter')
    activeLocalPrinterChanged() {
        localStorage.setItem("active-compound-batch-printer", this.activeLocalPrinter?.name);
    }

    get hasLocalPrinters() {
        return !!this.localPrinters.length;
    }

    prettyPrinterName(printer: any) {
        if (!printer) return "No Printer Defined";
        if (!printer.serverName) return printer.name;
        return printer.name.replace(printer.serverName, "").replace(/\\/g, "");
    }
}
</script>

<style scoped>
.img-thumbnail {
    max-height: 300px;
    object-fit: contain;
}

/* The weigh-out is the one number the technician acts on; make it read as such. */
.ingredients-table .weigh-out {
    font-size: 1.05em;
    color: #1d62f0;
}

.ingredients-table .ingredient-row--overridden .weigh-out {
    color: #d9534f;
}

.ingredients-table .weigh-out-cell .btn-link {
    line-height: 1;
    vertical-align: baseline;
}

/* A step that weighs out an ingredient says which one, and from which lot, above the weight entry. */
.linked-step {
    background-color: #f5f7fa;
    border-left: 3px solid #1d62f0;
}
</style>
